import { useEffect, ReactNode, useState, useRef, CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { cva, type VariantProps } from 'class-variance-authority'
import { CSSTransition } from 'react-transition-group'
import { Slot } from '@radix-ui/react-slot'

import { cn } from 'utils/classMerge'

const tooltipVariants = cva('tw-z-[9999] tw-w-max tw-max-w-xs', {
  variants: {
    size: {
      sm: 'tw-px-2 tw-py-1 tw-text-xs',
      md: 'tw-px-3 tw-py-2 tw-text-sm',
      lg: 'tw-px-4 tw-py-3 tw-text-base',
    },
    variant: {
      default: 'tw-rounded-lg tw-bg-gray-900 tw-text-white tw-shadow-lg',
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'default',
  },
})

const arrowVariants = cva(
  'tw-absolute tw-h-2 tw-w-2 tw-rotate-45 tw-bg-gray-900',
  {
    variants: {
      position: {
        top: 'tw--bottom-1 tw-left-1/2 tw--translate-x-1/2',
        bottom: 'tw--top-1 tw-left-1/2 tw--translate-x-1/2',
        left: 'tw--right-1 tw-top-1/2 tw--translate-y-1/2',
        right: 'tw--left-1 tw-top-1/2 tw--translate-y-1/2',
      },
    },
    defaultVariants: {
      position: 'top',
    },
  },
)

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'
type TriggerType = 'hover' | 'click'

export interface TooltipProps extends VariantProps<typeof tooltipVariants> {
  children: ReactNode
  content: ReactNode
  position?: TooltipPosition
  triggerType?: TriggerType
  className?: string
  showArrow?: boolean
  testId?: string
  timeoutToClose?: number
  asChild?: boolean
  useDefaultCursor?: boolean
}

const Tooltip = ({
  children,
  content,
  position = 'top',
  triggerType = 'hover',
  size,
  variant,
  className,
  showArrow = true,
  testId = 'tooltip',
  timeoutToClose,
  asChild = false,
  useDefaultCursor = false,
}: TooltipProps) => {
  const Component = asChild ? Slot : 'div'

  const [isShowing, setIsShowing] = useState(false)
  const [actualPosition, setActualPosition] =
    useState<TooltipPosition>(position)
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({})
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setActualPosition(position)
  }, [position])

  useEffect(() => {
    if (isShowing && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let newPosition: TooltipPosition = actualPosition
      let style: CSSProperties = {}
      const spacing = 8

      const calculatePosition = (
        pos: TooltipPosition,
      ): { left: number; top: number } => {
        switch (pos) {
          case 'top':
            return {
              left:
                triggerRect.left +
                triggerRect.width / 2 -
                tooltipRect.width / 2,
              top: triggerRect.top - tooltipRect.height - spacing,
            }
          case 'bottom':
            return {
              left:
                triggerRect.left +
                triggerRect.width / 2 -
                tooltipRect.width / 2,
              top: triggerRect.bottom + spacing,
            }
          case 'left':
            return {
              left: triggerRect.left - tooltipRect.width - spacing,
              top:
                triggerRect.top +
                triggerRect.height / 2 -
                tooltipRect.height / 2,
            }
          case 'right':
            return {
              left: triggerRect.right + spacing,
              top:
                triggerRect.top +
                triggerRect.height / 2 -
                tooltipRect.height / 2,
            }
          default:
            return { left: 0, top: 0 }
        }
      }

      const { left, top } = calculatePosition(position)
      style = { left, top }

      if (position === 'top' && top < 0) {
        newPosition = 'bottom'
      } else if (
        position === 'bottom' &&
        top + tooltipRect.height > viewportHeight
      ) {
        newPosition = 'top'
      } else if (position === 'left' && left < 0) {
        newPosition = 'right'
      } else if (
        position === 'right' &&
        left + tooltipRect.width > viewportWidth
      ) {
        newPosition = 'left'
      }

      if (newPosition !== position) {
        const newPos = calculatePosition(newPosition)
        style = { left: newPos.left, top: newPos.top }
      }

      if (['top', 'bottom'].includes(newPosition)) {
        if (left < 0) {
          style.left = spacing
          newPosition = 'right'
        } else if (left + tooltipRect.width > viewportWidth) {
          style.left = viewportWidth - tooltipRect.width - spacing
          newPosition = 'left'
        }
      }

      setActualPosition(newPosition)
      setTooltipStyle({
        ...style,
        position: 'fixed',
        zIndex: 99999,
      })
    }
  }, [isShowing, position, actualPosition])

  const hoverProps = {
    onMouseEnter: () => setIsShowing(true),
    onMouseLeave: () => {
      if (timeoutToClose) {
        setTimeout(() => setIsShowing(false), timeoutToClose)
        return
      }
      setIsShowing(false)
    },
  }

  const clickProps = {
    onClick: () => setIsShowing((prev) => !prev),
  }

  const triggerProps = triggerType === 'click' ? clickProps : hoverProps

  return (
    <>
      <Component
        ref={triggerRef}
        className={cn('tw-inline-block', {
          'tw-cursor-help': !useDefaultCursor,
        })}
        {...triggerProps}
      >
        {children}
      </Component>

      {createPortal(
        <CSSTransition
          in={isShowing}
          timeout={300}
          classNames={`fade-${actualPosition}`}
          unmountOnExit
          nodeRef={tooltipRef}
        >
          <div
            ref={tooltipRef}
            style={tooltipStyle}
            className={cn(tooltipVariants({ size, variant, className }))}
            data-testid={testId}
          >
            <div className="tw-relative tw-z-10">{content}</div>
            {showArrow && (
              <div
                className={cn(arrowVariants({ position: actualPosition }))}
              />
            )}
          </div>
        </CSSTransition>,
        document.body,
      )}
    </>
  )
}

Tooltip.displayName = 'Tooltip'

export { Tooltip, tooltipVariants }
